import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CxmDepositForm,
  CxmRouteLoader,
  CxmWithdrawalForm,
  PageLayout,
} from "src/components";
import { useCxmLoaderSequence } from "src/hooks";
import { Scenario, useCxmMockDeposit, useCxmMockWithdrawal } from "./useMockFlows";
import "./cxm-form-demo.scss";

type Flow = "withdrawal" | "deposit";

const FLOWS: { id: Flow; label: string }[] = [
  { id: "withdrawal", label: "payout" },
  { id: "deposit", label: "deposit" },
];

const SCENARIOS: { id: Scenario; label: string }[] = [
  { id: "normal", label: "granted" },
  { id: "empty", label: "no rules" },
  { id: "failed", label: "catalogue 502" },
];

/**
 * The CXM forms on a fake gateway: the shipped components and the shipped
 * validation, with `cxmService` replaced by fixtures taken from the partner's
 * test environment. Light theme on the left, dark on the right, one state
 * driving both — so typing in either half moves the other, and the two are
 * always the same screen in two skins.
 *
 * It exists because the real flow needs a VPN, a session token and a payment
 * route before a single field is visible, which makes reviewing the styling
 * expensive and, when the gateway is down, impossible.
 *
 * Every screen is reachable: the payout flow starts on the bank rail with no
 * saved account, so `Add a payout account` and the schema-built form are two
 * clicks away, and the scenario switch covers the two terminal states an
 * operator's configuration can produce. Not linked from anywhere; review only.
 */
const CxmFormDemo: React.FC = (): JSX.Element => {
  const { t } = useTranslation();

  const [flow, setFlow] = useState<Flow>("withdrawal");
  const [scenario, setScenario] = useState<Scenario>("normal");
  const [run, setRun] = useState(0);

  const withdrawal = useCxmMockWithdrawal(scenario, run);
  const deposit = useCxmMockDeposit(scenario, run);

  /* Both halves are light/dark specimens of one screen, so the page owns the
     root theme rather than inheriting whatever `?theme=` asked for. */
  useEffect(() => {
    const root = document.documentElement;
    const previous = root.getAttribute("data-theme");
    const apply = () => root.setAttribute("data-theme", "light");
    apply();
    // `useThemeInitializer` runs in the Router, i.e. after this child's
    // effects, and would put the URL's theme back.
    const settle = window.setTimeout(apply, 0);
    return () => {
      window.clearTimeout(settle);
      if (previous) root.setAttribute("data-theme", previous);
    };
  }, []);

  const loading = flow === "withdrawal" ? withdrawal.methodsLoading : deposit.methodsLoading;
  const loader = useCxmLoaderSequence(loading);

  const replay = (next?: { flow?: Flow; scenario?: Scenario }) => {
    if (next?.flow) setFlow(next.flow);
    if (next?.scenario) setScenario(next.scenario);
    setRun((r) => r + 1);
  };

  const label =
    flow === "withdrawal" ? t("CXM.WITHDRAWAL.LOADING") : t("CXM.DEPOSIT.LOADING");

  const screen = (
    <>
      {!loading && loader.revealed && (
        <PageLayout centered>
          {flow === "withdrawal" ? (
            <CxmWithdrawalForm withdrawal={withdrawal} />
          ) : (
            <CxmDepositForm deposit={deposit} />
          )}
        </PageLayout>
      )}
      {loader.phase !== "done" && (
        <CxmRouteLoader label={label} leaving={loader.phase === "leaving"} />
      )}
    </>
  );

  return (
    <div className="cxm-form-demo">
      <section className="cxm-form-demo__pane" data-theme="light">
        {screen}
        <span className="cxm-form-demo__tag">light</span>
      </section>

      <section className="cxm-form-demo__pane" data-theme="dark">
        {screen}
        <span className="cxm-form-demo__tag">dark</span>
      </section>

      <div className="cxm-form-demo__hud">
        <div className="cxm-form-demo__group">
          {FLOWS.map((option) => (
            <button
              key={option.id}
              type="button"
              className="cxm-form-demo__key"
              aria-pressed={flow === option.id}
              onClick={() => replay({ flow: option.id })}
            >
              {option.label}
            </button>
          ))}
        </div>

        <div className="cxm-form-demo__group">
          {SCENARIOS.map((option) => (
            <button
              key={option.id}
              type="button"
              className="cxm-form-demo__key"
              aria-pressed={scenario === option.id}
              onClick={() => replay({ scenario: option.id })}
            >
              {option.label}
            </button>
          ))}
        </div>

        <button type="button" className="cxm-form-demo__key" onClick={() => replay()}>
          replay
        </button>
      </div>
    </div>
  );
};

export default CxmFormDemo;
